import { runInAction } from 'mobx';

import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import QuarantinedFileStore, { QUARANTINED } from 'stores/quarantinedFile/QuarantinedFileStore';
import { SortDirection } from 'constants/SortDirection';
import { getConditions, getDefaultFilter, getListConditions } from 'stores/_services/filterService';
import { IDataFetchOptions } from '../../types';
import { buildOptions, buildWhereFilter, get, getCount, getPage, post } from 'stores/_services/dataService';
import { buildCsvDownloadUrl } from 'stores/_services/downloadService';

const SEARCH_FIELDS = ['threat', 'name', 'alertName'];

export default class QuarantinedFileListStore extends BaseListStore<QuarantinedFileStore> {
    activeQuarantinedFileCount = 0;

    constructor() {
        super({
            itemStore: QuarantinedFileStore,
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC,
            defaultFilter: getDefaultFilter({ filters: ['organization', 'location', 'action'] })
        });
    }

    override selectAll(): void {
        this.selectedIds = this.ids
            .filter((id) => this.data.get(id)?.quarantinedFile.status === QUARANTINED)
            .reduce((accumulator, value) => {
                return { ...accumulator, [value]: true };
            }, {});
    }

    async getQuarantinedFiles(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            }
        };

        const { data } = await getPage('quarantinedFiles', options);
        const totalCount = await getCount('quarantinedFiles', { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);

        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getQuarantinedFile(quarantinedFileId: string): Promise<void> {
        const { data } = await get(`quarantinedFiles/${quarantinedFileId}`);
        this._updateData(data);
    }

    async getActiveQuarantinedFileCount(): Promise<void> {
        const count = await getCount('quarantinedFiles', { where: { action: { eq: null } } });
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.activeQuarantinedFileCount = count;
        });
    }

    async deleteFile(id: string): Promise<void> {
        const options = {
            queryParams: { where: { id } }
        };

        await post('quarantinedFiles/createDeleteFileJobs', null, options);

        await this.getActiveQuarantinedFileCount();
        await this.getQuarantinedFile(id);
    }

    async deleteFiles(pageSize = DEFAULT_LIMIT): Promise<void> {
        const conditions = getListConditions(this.selectedIdList, this.filter);

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('quarantinedFiles/createDeleteFileJobs', null, { queryParams: { where } });

        this.deselectAll();
        this.getActiveQuarantinedFileCount();
        this.getQuarantinedFiles({ reset: true }, 1, pageSize);
    }

    async restoreFile(id: string): Promise<void> {
        const options = {
            queryParams: { where: { id } }
        };

        await post('quarantinedFiles/createRestoreFileJobs', null, options);

        await this.getActiveQuarantinedFileCount();
        await this.getQuarantinedFile(id);
    }

    async restoreFiles(pageSize = DEFAULT_LIMIT): Promise<void> {
        const conditions = getListConditions(this.selectedIdList, this.filter);

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('quarantinedFiles/createRestoreFileJobs', null, { queryParams: { where } });

        this.deselectAll();
        this.getActiveQuarantinedFileCount();
        this.getQuarantinedFiles({ reset: true }, 1, pageSize);
    }

    async exportToCsv(): Promise<void> {
        const conditions = getListConditions(this.selectedIdList, this.filter);
        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            }
        };

        const query = buildOptions(options);

        window.location.href = await buildCsvDownloadUrl('quarantinedFiles', query);
    }
}
